import React, { useState, useCallback } from 'react';
import StaffSectionLayout from '../../../components/staff/StaffSectionLayout';
import ContentBox from '../../../components/ContentBox';
import { FpButton, FpModal, TextInput, RarityPill } from '../../../components/frankopop/frankopopShared';
import { useFrankopopData, fetchAuditFeed, searchLedger, fetchAdminInstanceHistory, relativeTime } from '../../../data/frankopop';

const EVENT_LABELS = {
    box_acquired: 'Box',
    minted: 'Opened',
    unpackaged: 'Unboxed',
    quick_sold: 'Quick sold',
    traded: 'Traded',
};

// Who did what, in words - the ledger stores user IDs, the API adds names.
function describeEvent(event) {
    const who = (name) => name || 'Someone';
    switch (event.eventType) {
        case 'minted': return `${who(event.toName || event.actorName)} pulled it from a box`;
        case 'unpackaged': return `${who(event.actorName)} took it out of its packaging`;
        case 'quick_sold': return `${who(event.fromName)} quick sold it`;
        case 'traded': return `${who(event.fromName)} traded it to ${who(event.toName)}`;
        default: return null;
    }
}

function LedgerDetail({ instanceId, onClose }) {
    const { data, loading } = useFrankopopData(useCallback(() => fetchAdminInstanceHistory(instanceId), [instanceId]), null);

    return (
        <FpModal title="FrankoPop history" onClose={onClose} maxWidth="max-w-lg">
            {loading || !data ? (
                <p className="text-sm text-gray-500">Loading...</p>
            ) : (
                <div className="space-y-4">
                    <div>
                        <p className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-gray-900">{data.figure?.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{data.set?.name} · Serial #{data.serialNumber} · {data.status === 'quick_sold' ? 'Quick sold' : data.inCase ? 'Sealed' : 'Unboxed'}</p>
                        {data.figure?.rarityName && <RarityPill name={data.figure.rarityName} colour={data.figure.rarityColour} className="mt-2" />}
                        <p className="text-xs text-gray-600 mt-2">
                            Current owner: <strong>{data.currentOwnerName || 'Unknown'}</strong>
                            {data.listed && <span className="text-amber-700"> · listed for trade</span>}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-1 font-mono">{data.id}</p>
                    </div>
                    <div className="space-y-2">
                        {data.history.map((event) => (
                            <div key={event.id} className="border border-gray-200 bg-white px-3 py-2 text-sm">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-maroon">
                                        {EVENT_LABELS[event.eventType] || event.eventType}
                                    </span>
                                    <span className="text-[11px] text-gray-400">{relativeTime(event.at)}</span>
                                </div>
                                {describeEvent(event) && <p className="text-sm text-gray-800 mt-1">{describeEvent(event)}</p>}
                                {event.notes && <p className="text-[11px] text-gray-400 mt-0.5">{event.notes}</p>}
                                {event.frampsAmount != null && (
                                    <p className="text-xs text-gray-500 mt-0.5">{event.frampsAmount} framps</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </FpModal>
    );
}

export default function FrankoPopAudit() {
    const { data: events, loading: feedLoading } = useFrankopopData(useCallback(() => fetchAuditFeed(50), []), []);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState(null);

    const runSearch = async () => {
        const q = query.trim();
        if (!q) return;
        setSearching(true);
        const res = await searchLedger(q);
        setResults(res);
        setSearching(false);
    };

    return (
        <StaffSectionLayout>
            <ContentBox theme="default" tint="red" header="FrankoPop - ledger search">
                <p className="text-sm text-gray-600 mb-3">
                    Search by Habbo name, FrankoPop name, or a specific FrankoPop&apos;s ID to see its full journey -
                    every set it came from, who owned it, and where it went.
                </p>
                <div className="flex gap-2 max-w-lg">
                    <TextInput
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Habbo name, FrankoPop name or ID"
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                    />
                    <FpButton variant="primary" onClick={runSearch} disabled={!query.trim()}>Search</FpButton>
                </div>

                {searching && <p className="text-sm text-gray-500 mt-3">Searching...</p>}

                {results && !searching && (
                    results.length === 0 ? (
                        <p className="text-sm text-gray-500 mt-3">No matches.</p>
                    ) : (
                        <div className="space-y-2 mt-3">
                            {results.map((r) => (
                                <button
                                    key={r.id}
                                    type="button"
                                    onClick={() => setSelectedInstance(r.id)}
                                    className="w-full text-left flex flex-wrap items-center gap-3 border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                                >
                                    <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-800">{r.figureName}</span>
                                    <span className="text-gray-500">{r.setName}</span>
                                    <span className="text-gray-500">owned by {r.ownerName}</span>
                                    <span className={`ml-auto font-['Jersey_10'] uppercase text-[11px] tracking-[1px] ${r.status === 'quick_sold' ? 'text-red-500' : 'text-green-600'}`}>
                                        {r.status === 'quick_sold' ? 'Sold' : r.inCase ? 'Sealed' : 'Unboxed'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )
                )}
            </ContentBox>

            <ContentBox theme="default" tint="red" header="Recent activity">
                {feedLoading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                ) : !events || events.length === 0 ? (
                    <p className="text-sm text-gray-500">No activity yet.</p>
                ) : (
                    <div className="space-y-2">
                        {events.map((e, i) => (
                            <div
                                key={i}
                                className={`border border-gray-200 bg-white px-3 py-2 text-sm flex items-center justify-between gap-3 ${e.instanceId ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                onClick={() => e.instanceId && setSelectedInstance(e.instanceId)}
                            >
                                <span className="text-gray-700">{e.description}</span>
                                <span className="text-[11px] text-gray-400 shrink-0">{relativeTime(e.at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </ContentBox>

            {selectedInstance && <LedgerDetail instanceId={selectedInstance} onClose={() => setSelectedInstance(null)} />}
        </StaffSectionLayout>
    );
}
